import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type UIEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, ChevronDown, Info, Loader2, Search, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

// ------------------------------------------------------------------- Button

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-300',
  secondary:
    'bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600',
  outline:
    'border border-subtle bg-transparent hover:bg-sunken',
  ghost: 'bg-transparent hover:bg-sunken',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 disabled:bg-red-300',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // 44px minimum touch target on md and lg — the POS is used on tablets.
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: LucideIcon;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon: Icon, fullWidth, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : Icon ? <Icon className="size-4" aria-hidden /> : null}
      {children}
    </button>
  );
});

// -------------------------------------------------------------------- Field

interface FieldWrapperProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, htmlFor, children }: FieldWrapperProps) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={htmlFor} className="block text-sm font-medium">
          {label}
          {required && <span className="ml-0.5 text-red-500" aria-hidden>*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
      ) : hint ? (
        <p className="text-sm text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

const CONTROL_CLASS =
  'w-full rounded-lg border border-subtle bg-transparent px-3 text-sm ' +
  'placeholder:text-muted focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  icon?: LucideIcon;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, icon: Icon, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <Field label={label} hint={hint} error={error} required={props.required} htmlFor={inputId}>
      <div className="relative">
        {Icon && (
          <Icon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          className={cn(CONTROL_CLASS, 'h-11', Icon && 'pl-9', error && 'border-red-500', className)}
          {...props}
        />
      </div>
    </Field>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Placeholder for the search box inside the list. */
  searchPlaceholder?: string;
  /**
   * For lists paged from the server: called when the user scrolls to the end
   * of what has been loaded. Options already passed as children are revealed
   * in batches on scroll without it.
   */
  onLoadMore?: () => void;
  hasMore?: boolean;
  /** A page or a search is on its way; a spinner shows instead of "nothing matches". */
  loadingMore?: boolean;
  /**
   * Hands searching to the caller, so it can ask the server: a list paged from
   * the server can only be filtered by the server. Called a moment after typing
   * stops, and with '' whenever the list opens. The options are shown as given.
   */
  onSearch?: (query: string) => void;
}

interface SelectOption {
  value: string;
  label: string;
  disabled: boolean;
}

/** How many options are drawn at once; more are revealed as the list scrolls. */
const OPTION_BATCH = 50;
/** How long typing must pause before a server search is asked for. */
const SEARCH_DELAY_MS = 300;
const LIST_MAX_HEIGHT = 320;

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

/** Reads the `<option>` children callers already pass, fragments and arrays included. */
function collectOptions(children: ReactNode, into: SelectOption[] = []): SelectOption[] {
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: unknown; disabled?: boolean; children?: ReactNode }>(child)) return;
    if (child.type === 'option') {
      const label = textOf(child.props.children);
      into.push({
        value: child.props.value === undefined ? label : String(child.props.value),
        label,
        disabled: Boolean(child.props.disabled),
      });
    } else if (child.type === Fragment || child.type === 'optgroup') {
      collectOptions(child.props.children, into);
    }
  });
  return into;
}

function matches(option: SelectOption, query: string): boolean {
  const haystack = option.label.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((term) => haystack.includes(term));
}

/**
 * A searchable dropdown.
 *
 * The browser's own list is replaced because it cannot be searched and, on
 * Windows, draws its options in the page's text colour on a white background —
 * white on white in dark mode. The list here is always dark text on white.
 *
 * A native `<select>` stays underneath, visually hidden, and remains the real
 * control: it carries the id the label points at, the value, `name` and
 * `required` for forms, and it fires the caller's `onChange` — so callers pass
 * `<option>` children and read `event.target.value` exactly as before.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label, hint, error, className, id, children, searchPlaceholder = 'Search…',
    onLoadMore, hasMore, loadingMore, onSearch, ...props
  },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const listId = `${selectId}-list`;
  const valueId = `${selectId}-value`;

  const nativeRef = useRef<HTMLSelectElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => collectOptions(children), [children]);
  const controlled = props.value !== undefined;
  const [innerValue, setInnerValue] = useState<string>(() =>
    props.defaultValue !== undefined ? String(props.defaultValue) : (options[0]?.value ?? ''),
  );
  const value = controlled ? String(props.value) : innerValue;
  const found = options.find((option) => option.value === value);

  // A server search replaces the options, and the chosen one is usually not
  // among the results. Its label is kept so the trigger still names it, and so
  // the native select still holds the value for the form.
  const remembered = useRef<SelectOption | null>(null);
  if (found) remembered.current = found;
  const selected = found ?? (remembered.current?.value === value ? remembered.current : undefined);
  const missing = found ? null : selected;
  const searchTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(searchTimer.current), []);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const [visible, setVisible] = useState(OPTION_BATCH);
  const [position, setPosition] = useState<CSSProperties>({});

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    // The server has already filtered; only the blank "none" option is dropped.
    if (onSearch) return options.filter((option) => option.value);
    return options.filter((option) => matches(option, query));
  }, [options, query, onSearch]);
  const shown = filtered.slice(0, visible);

  const setRefs = (node: HTMLSelectElement | null): void => {
    nativeRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const place = (): void => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const openUp = below < 240 && above > below;
    const room = Math.min(LIST_MAX_HEIGHT + 56, openUp ? above : below);
    setPosition({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 192),
      maxHeight: room,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  };

  const openList = (): void => {
    if (props.disabled) return;
    place();
    setQuery('');
    setVisible(OPTION_BATCH);
    window.clearTimeout(searchTimer.current);
    onSearch?.('');
    const index = options.findIndex((option) => option.value === value);
    setActive(index);
    if (index >= OPTION_BATCH) setVisible(index + OPTION_BATCH);
    setOpen(true);
  };

  const close = (refocus = true): void => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const choose = (option: SelectOption): void => {
    if (option.disabled) return;
    close();
    const native = nativeRef.current;
    if (!native || option.value === value) return;
    // Change the real select and let it announce the change, so the caller's
    // onChange receives a genuine change event from a genuine <select>.
    native.value = option.value;
    native.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // Keep the list pinned to its trigger while anything scrolls or resizes.
  useLayoutEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const onPointer = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!popupRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  // Keyboard movement follows the highlighted option into view.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  const revealMore = (): void => {
    if (visible < filtered.length) setVisible((count) => count + OPTION_BATCH);
    else if (hasMore && !loadingMore) onLoadMore?.();
  };

  const onListScroll = (event: UIEvent<HTMLUListElement>): void => {
    const list = event.currentTarget;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 48) revealMore();
  };

  const move = (step: number): void => {
    if (filtered.length === 0) return;
    let next = active;
    for (let tries = 0; tries < filtered.length; tries += 1) {
      next = Math.min(Math.max(next + step, 0), filtered.length - 1);
      if (!filtered[next].disabled) break;
    }
    if (next >= visible) setVisible(next + OPTION_BATCH);
    setActive(next);
  };

  const onSearchKey = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); move(1); break;
      case 'ArrowUp': event.preventDefault(); move(-1); break;
      case 'PageDown': event.preventDefault(); move(10); break;
      case 'PageUp': event.preventDefault(); move(-10); break;
      case 'Enter':
        event.preventDefault();
        if (filtered[active]) choose(filtered[active]);
        break;
      case 'Escape':
        // Close the list, not the modal it may be sitting in.
        event.preventDefault();
        event.nativeEvent.stopPropagation();
        close();
        break;
      case 'Tab':
        close(false);
        break;
    }
  };

  const onTriggerKey = (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openList();
    }
  };

  const accessibleName = label ?? props['aria-label'] ?? 'an option';

  return (
    <Field label={label} hint={hint} error={error} required={props.required} htmlFor={selectId}>
      <div className="relative">
        <select
          ref={setRefs}
          id={selectId}
          tabIndex={-1}
          aria-invalid={error ? true : undefined}
          className="sr-only"
          {...props}
          onChange={(event) => {
            if (!controlled) setInnerValue(event.target.value);
            props.onChange?.(event);
          }}
          // The label points here; hand focus on to the visible control.
          onFocus={() => triggerRef.current?.focus()}
        >
          {children}
          {missing && <option value={missing.value}>{missing.label}</option>}
        </select>

        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-label={`Choose ${accessibleName}`}
          aria-describedby={valueId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-invalid={error ? true : undefined}
          disabled={props.disabled}
          onClick={() => (open ? close() : openList())}
          onKeyDown={onTriggerKey}
          className={cn(
            CONTROL_CLASS,
            'flex h-11 items-center justify-between gap-2 text-left',
            error && 'border-red-500',
            className,
          )}
        >
          <span id={valueId} className={cn('truncate', !selected?.value && 'text-muted')}>
            {selected?.label || ' '}
          </span>
          <ChevronDown className={cn('size-4 shrink-0 text-muted transition-transform', open && 'rotate-180')} aria-hidden />
        </button>

        {open && createPortal(
          <div
            ref={popupRef}
            style={position}
            className="z-[60] flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 shadow-xl [color-scheme:light]"
          >
            <div className="relative border-b border-slate-200 p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden />
              <input
                ref={searchRef}
                type="text"
                role="searchbox"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={`Search ${accessibleName}`}
                aria-controls={listId}
                aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
                autoComplete="off"
                onChange={(event) => {
                  const next = event.target.value;
                  setQuery(next);
                  setVisible(OPTION_BATCH);
                  setActive(next ? 0 : -1);
                  if (listRef.current) listRef.current.scrollTop = 0;
                  if (onSearch) {
                    window.clearTimeout(searchTimer.current);
                    searchTimer.current = window.setTimeout(() => onSearch(next.trim()), SEARCH_DELAY_MS);
                  }
                }}
                onKeyDown={onSearchKey}
                className="h-9 w-full rounded-md border border-slate-300 bg-white pl-8 pr-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={accessibleName}
              onScroll={onListScroll}
              className="flex-1 overflow-y-auto py-1"
              style={{ maxHeight: LIST_MAX_HEIGHT }}
            >
              {shown.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <li
                    key={`${option.value}-${index}`}
                    id={`${listId}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    onMouseEnter={() => !option.disabled && setActive(index)}
                    // mousedown, so the search box keeps focus until the choice is made
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm',
                      index === active && 'bg-brand-50',
                      isSelected && 'font-medium',
                      !option.value && 'text-slate-500',
                      option.disabled && 'cursor-not-allowed text-slate-400',
                    )}
                  >
                    <span className="truncate">{option.label || ' '}</span>
                    {isSelected && <Check className="size-4 shrink-0 text-brand-600" aria-hidden />}
                  </li>
                );
              })}

              {filtered.length === 0 && !loadingMore && (
                <li className="px-3 py-6 text-center text-sm text-slate-500">
                  {query ? `Nothing matches “${query}”` : 'Nothing to choose from'}
                </li>
              )}

              {(visible < filtered.length || hasMore || loadingMore) && (
                <li className="flex justify-center py-2 text-slate-400" aria-hidden>
                  <Loader2 className="size-4 animate-spin" />
                </li>
              )}
            </ul>
          </div>,
          document.body,
        )}
      </div>
    </Field>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const areaId = id ?? generatedId;

  return (
    <Field label={label} hint={hint} error={error} required={props.required} htmlFor={areaId}>
      <textarea
        ref={ref}
        id={areaId}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL_CLASS, 'min-h-24 py-2.5', error && 'border-red-500', className)}
        {...props}
      />
    </Field>
  );
});

export function Checkbox({
  label,
  description,
  className,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }) {
  const generatedId = useId();
  const boxId = id ?? generatedId;

  return (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        id={boxId}
        className={cn('mt-0.5 size-4 rounded border-subtle text-brand-600 focus:ring-brand-500', className)}
        {...props}
      />
      <div>
        <label htmlFor={boxId} className="text-sm font-medium">{label}</label>
        {description && <p className="text-sm text-muted">{description}</p>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- Card

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('surface rounded-xl', className)}>{children}</div>;
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-subtle px-5 py-4">
      <div>
        <h2 className="font-semibold">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-secondary">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

// -------------------------------------------------------------------- Badge

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  info: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  brand: 'bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-100',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ------------------------------------------------------------------- States

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-muted', className)} aria-hidden />;
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16" role="status">
      <Spinner className="size-6" />
      <p className="text-sm text-secondary">{label}</p>
    </div>
  );
}

export function EmptyState({
  icon: Icon = Info,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="rounded-full bg-sunken p-3">
        <Icon className="size-6 text-muted" aria-hidden />
      </div>
      <div>
        <h3 className="font-medium">{title}</h3>
        {description && <p className="mx-auto mt-1 max-w-md text-sm text-secondary">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center" role="alert">
      <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/40">
        <AlertTriangle className="size-6 text-red-600 dark:text-red-300" aria-hidden />
      </div>
      <div>
        <h3 className="font-medium">{title}</h3>
        {message && <p className="mx-auto mt-1 max-w-md text-sm text-secondary">{message}</p>}
      </div>
      {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    info: { box: 'bg-sky-50 border-sky-200 dark:bg-sky-950/40 dark:border-sky-900', icon: Info, color: 'text-sky-600 dark:text-sky-300' },
    success: { box: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900', icon: Check, color: 'text-emerald-600 dark:text-emerald-300' },
    warning: { box: 'bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900', icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-300' },
    danger: { box: 'bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-900', icon: AlertTriangle, color: 'text-red-600 dark:text-red-300' },
  }[tone];

  const Icon = tones.icon;

  return (
    <div className={cn('flex gap-3 rounded-lg border p-4', tones.box)} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon className={cn('mt-0.5 size-5 shrink-0', tones.color)} aria-hidden />
      <div className="text-sm">
        {title && <p className="font-medium">{title}</p>}
        <div className={title ? 'mt-0.5 text-secondary' : ''}>{children}</div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- Modal

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl',
          'surface shadow-xl',
          widths[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-subtle px-5 py-4">
          <div>
            <h2 className="font-semibold">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-secondary">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 hover:bg-sunken"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-subtle px-5 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

/** A destructive action always asks first. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Delete',
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <p className="text-sm text-secondary">{message}</p>
    </Modal>
  );
}

// -------------------------------------------------------------------- Table

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  // Wide tables scroll inside their own container; the page never scrolls sideways.
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full min-w-[40rem] border-collapse text-sm', className)}>
        {children}
      </table>
    </div>
  );
}

export function Th({
  children,
  className,
  numeric,
}: {
  children?: ReactNode;
  className?: string;
  numeric?: boolean;
}) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-subtle px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted',
        numeric && 'text-right',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  numeric,
}: {
  children?: ReactNode;
  className?: string;
  numeric?: boolean;
}) {
  return (
    <td className={cn('border-b border-subtle px-4 py-3', numeric && 'text-right tabular', className)}>
      {children}
    </td>
  );
}

/** How many rows a list shows at once. */
export const PAGE_SIZES = [25, 50, 100, 200];

export function Pagination({
  page,
  totalPages,
  total,
  onPageChange,
  limit,
  onLimitChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Passing these two adds a page-size control beside the buttons. */
  limit?: number;
  onLimitChange?: (limit: number) => void;
}) {
  const sizeControl = limit !== undefined && onLimitChange ? (
    <label className="flex items-center gap-2 text-sm text-muted">
      Show
      <select
        value={limit}
        aria-label="Rows per page"
        onChange={(event) => onLimitChange(Number(event.target.value))}
        className="rounded-lg border border-subtle bg-transparent px-2 py-1 text-sm"
      >
        {PAGE_SIZES.map((size) => (
          <option key={size} value={size}>{size}</option>
        ))}
      </select>
    </label>
  ) : null;

  // One page of results still deserves the size control: the reason there is
  // one page may be that the size is 200.
  if (totalPages <= 1) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <p className="text-sm text-muted">
          {total} {total === 1 ? 'record' : 'records'}
        </p>
        {sizeControl}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <p className="text-sm text-muted">
        Page {page} of {totalPages} · {total} records
      </p>
      <div className="flex items-center gap-2">
        {sizeControl}
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-secondary">{description}</p>}
      </div>
      {action}
    </div>
  );
}
